import { isAxiosError } from 'axios';
import {
  DownloadResponsePayload,
  FileUploadType,
  FileUploadCancellationRequest,
} from '@aslanpay/file-processing-service-client';
import { getApiClient } from '@aslanpay/portal-common-lib';

import { GiftingFileUploadType } from 'app/hooks/useFileProcessingService/types';

/**
 * Centralized API handler for file processing service operations
 * Uses direct HTTP calls with portal-common-lib API client for authentication
 *
 * Key Points:
 * - Authentication: Uses getApiClient() from @aslanpay/portal-common-lib for automatic authentication
 * - Base URL: All FPS calls use /fps/ prefix (e.g., /fps/files, /fps/files/upload)
 * - Error Handling: Comprehensive logging and error handling with detailed console output
 * - Type Safety: Full TypeScript interfaces for all FPS API responses
 * - Caching: React Query integration with 30-second stale time and 5-minute cache time
 * - Conditional Fetching: Only fetches when conditions are met (company has non-salaried workers, tab is active)
 *
 * The FPS client is essentially a thin wrapper around the common lib's getApiClient() that adds
 * FPS-specific endpoints, logging, and error handling while maintaining the same authentication
 * mechanism as the rest of the portal.
 */
class FileProcessingApiHandler {
  /**
   * Get the authenticated API client instance using portal-common-lib
   * This uses the same authentication mechanism as other portal services
   */
  private static getApiClient() {
    return getApiClient();
  }

  /**
   * Get file by ID with authentication using direct HTTP call
   */
  static async getFileById(fileId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/${fileId}`;

    // eslint-disable-next-line no-console
    console.log('Getting file by ID:', fileId);

    try {
      const response = await apiClient.get(url);
      // eslint-disable-next-line no-console
      console.log('File details response:', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get file details:', error);
      throw error;
    }
  }

  /**
   * Get all files with authentication using direct HTTP call
   * This matches the back office implementation
   */
  static async getAllFiles(
    companyId: string,
    fileType: FileUploadType = 'APPROVED_EARNINGS_TIMESHEET',
    page = 0,
    size = 1000
  ) {
    const apiClient = this.getApiClient();
    const url = '/fps/files';
    const params = {
      type: fileType,
      companyId,
      page,
      size,
    };

    try {
      const response = await apiClient.get(url, { params });

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('API call failed:', error);
      throw error;
    }
  }

  /**
   * Get upload pre-signed URL with authentication using direct HTTP call
   */
  static async getUploadPreSignedUrl(
    fileName: string,
    companyId: string,
    uploadType: GiftingFileUploadType = 'APPROVED_EARNINGS_TIMESHEET',
    payCalendarId?: string
  ) {
    const apiClient = this.getApiClient();
    const url = '/fps/files/upload';
    const params = {
      fileName,
      uploadType,
      companyId,
      ...(payCalendarId && { payCalendarId }),
    };

    // eslint-disable-next-line no-console
    console.log('Getting upload URL with params:', { fileName, uploadType, companyId });

    try {
      const response = await apiClient.get(url, { params });
      // eslint-disable-next-line no-console
      console.log('Upload URL response:', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get upload URL:', error);
      throw error;
    }
  }

  /**
   * Request file analysis with authentication using direct HTTP call
   */
  static async requestFileAnalysis(fileUploadId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/analyse`;

    // eslint-disable-next-line no-console
    console.log('Requesting file analysis for:', fileUploadId);

    try {
      const response = await apiClient.post(
        url,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      // eslint-disable-next-line no-console
      console.log('File analysis response:', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to request file analysis:', error);
      throw error;
    }
  }

  /**
   * Confirm file upload with authentication using direct HTTP call
   */
  static async confirmFileUpload(fileUploadId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/confirm`;

    // eslint-disable-next-line no-console
    console.log('Confirming file upload for:', fileUploadId);

    try {
      await apiClient.post(url, {}, { headers: { 'Content-Type': 'application/json' } });
      // eslint-disable-next-line no-console
      console.log('File upload confirmed successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to confirm file upload:', error);
      throw error;
    }
  }

  /**
   * Cancel file upload with authentication using direct HTTP call
   */
  static async cancelFileUpload(
    fileUploadId: string,
    cancellationRequest: FileUploadCancellationRequest
  ) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/cancel`;

    // eslint-disable-next-line no-console
    console.log('Cancelling file upload for:', fileUploadId, 'with request:', cancellationRequest);

    try {
      const response = await apiClient.post(url, cancellationRequest);
      // eslint-disable-next-line no-console
      console.log('File upload cancelled successfully:', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to cancel file upload:', error);
      throw error;
    }
  }

  /**
   * Get upload pre-signed URL specifically for benefit PDF documents (BENEFIT_DOCUMENT type)
   */
  static async getBenefitDocumentUploadPreSignedUrl(
    fileName: string,
    companyId: string
  ): Promise<{ id: string; url: string }> {
    const apiClient = this.getApiClient();
    const params = { fileName, uploadType: 'BENEFIT_DOCUMENT', companyId };

    try {
      const response = await apiClient.get('/fps/files/upload', { params });
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get benefit document upload URL:', error);
      throw error;
    }
  }

  /**
   * Verify a file upload after it has been PUT to S3
   */
  static async verifyUpload(fileUploadId: string): Promise<{ id: string; status: string }> {
    const apiClient = this.getApiClient();
    const url = `/fps/files/upload/${fileUploadId}/verify`;

    try {
      const response = await apiClient.post(url);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to verify upload:', error);
      throw error;
    }
  }

  /**
   * Get download pre-signed URL with authentication using direct HTTP call
   */
  static async getDownloadPreSignedUrl(fileId: string) {
    const apiClient = this.getApiClient();
    const url = `/fps/files/${fileId}/download`;

    // eslint-disable-next-line no-console
    console.log('Getting download URL for file:', fileId);

    try {
      const response = await apiClient.get(url);
      // eslint-disable-next-line no-console
      console.log('Download URL response:', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get download URL:', error);
      throw error;
    }
  }

  /**
   * Get a pre-signed URL for the resource file published under `resourceKey`: the signed-in
   * user's company file, else the global one. Null when neither exists (a 404).
   */
  static async getResourceDownloadPreSignedUrl(resourceKey: string): Promise<string | null> {
    const apiClient = this.getApiClient();

    try {
      const response = await apiClient.get<DownloadResponsePayload>(
        `/fps/files/resources/${resourceKey}/download`
      );
      return response.data?.url || null;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
}

export default FileProcessingApiHandler;
